import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Autocomplete,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete,
  Add,
  Calculate,
  TrendingUp,
  Info,
  Warning,
  CheckCircle,
  ShoppingCart,
  AccessTime,
  Layers,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import calculatorApi from '../services/calculatorApi';
import api from '../axiosConfig';
import './ServiceEditor.css';
import PriceLockDialog from './PriceLockDialog';

const fmt = (value) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);

const getTierRowStyle = (currentPrice, totalCost, recommendedPrice) => {
  const price = parseFloat(currentPrice) || 0;
  const cost  = parseFloat(totalCost)   || 0;
  const rec   = parseFloat(recommendedPrice) || 0;
  if (price <= 0) return {};
  if (cost > 0 && price < cost) return { color: '#c62828' };
  if (rec > 0 && price < rec)   return { color: '#BF360C' };
  return { color: '#2e7d32' };
};

const GapChip = ({ currentPrice, recommendedPrice, totalCost }) => {
  const price = parseFloat(currentPrice) || 0;
  const rec   = parseFloat(recommendedPrice) || 0;
  const cost  = parseFloat(totalCost) || 0;
  if (price <= 0 || rec <= 0) return null;
  const gap = price - rec;
  const belowCost = cost > 0 && price < cost;
  if (belowCost) {
    return (
      <Chip
        icon={<Warning sx={{ fontSize: '0.85rem !important' }} />}
        label="Below cost"
        size="small"
        sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.7rem' }}
      />
    );
  }
  if (gap < 0) {
    return (
      <Chip
        icon={<ArrowDownward sx={{ fontSize: '0.85rem !important' }} />}
        label={`${fmt(Math.abs(gap))} below rec.`}
        size="small"
        sx={{ backgroundColor: '#fff3e0', color: '#BF360C', fontSize: '0.7rem' }}
      />
    );
  }
  return (
    <Chip
      icon={<ArrowUpward sx={{ fontSize: '0.85rem !important' }} />}
      label={`${fmt(gap)} above rec.`}
      size="small"
      sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem' }}
    />
  );
};

const ServiceEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/solo') ? '/solo/calculator' : '/calculator';
  const { serviceId } = useParams();
  const isEditMode = serviceId && serviceId !== 'new';

  // NOTE: current_price (the old "Default Price") has been removed from the
  // editor. The backend now derives the flat fallback price from the tier prices.
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    duration: 60,
  });
  const [linkedServiceName, setLinkedServiceName] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [tierPrices, setTierPrices] = useState({});
  const [tierMeta, setTierMeta] = useState({});
  // Tier ids that had a saved price when the page loaded. Used so that clearing
  // one of them sends price 0, which tells the backend to delete the row.
  const [initialPricedTierIds, setInitialPricedTierIds] = useState([]);
  const [serviceProducts, setServiceProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState('');
  const [productIsOptional, setProductIsOptional] = useState(false);
  const [calculationResults, setCalculationResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationDialogOpen, setCalculationDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [priceLockOpen, setPriceLockOpen] = useState(false);
  const [priceLockData, setPriceLockData] = useState(null);
  const [pendingSaveWithLock, setPendingSaveWithLock] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState('');  // '' = today

  const steps = ['Basic Info', 'Products & Materials', 'Pricing Review'];

  useEffect(() => { loadInitialData(); }, [serviceId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [categoriesData, productsData, settingsData, tiersData] = await Promise.all([
        calculatorApi.getServiceCategories(),
        calculatorApi.getProducts?.() || Promise.resolve([]),
        calculatorApi.getBusinessSettings(),
        api.get('/calculator/tiers').then(r => r.data || []).catch(() => []),
      ]);
      setCategories(categoriesData);
      setProducts(productsData.data || productsData || []);
      setBusinessSettings(settingsData);
      setTiers(tiersData);

      if (isEditMode) {
        const serviceData = await calculatorApi.getService(serviceId);
        setFormData({
          name: serviceData.name || '',
          description: serviceData.description || '',
          category_id: serviceData.category_id || '',
          duration: serviceData.duration || 60,
        });
        if (serviceData.service_id && serviceData.linked) {
          try {
            const { data: salonService } = await api.get(`/services/${serviceData.service_id}`);
            setLinkedServiceName(salonService.name);
          } catch { setLinkedServiceName('Linked service'); }
        }
        const spData = await calculatorApi.getServiceProducts(serviceId);
        setServiceProducts(spData.data || spData || []);
        try {
          const tierPriceData = await api.get(`/calculator/services/${serviceId}/tier-prices`);
          const priceMap = {};
          const metaMap  = {};
          (tierPriceData.data || []).forEach(row => {
            priceMap[row.tier_id] = row.price;
            metaMap[row.tier_id]  = {
              recommended_price: row.recommended_price ?? null,
              total_cost:        row.total_cost        ?? null,
            };
          });
          setTierPrices(priceMap);
          setTierMeta(metaMap);
          setInitialPricedTierIds((tierPriceData.data || []).map(r => r.tier_id));
        } catch { /* no tier prices yet */ }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load service data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleTierPriceChange = (tierId, value) => setTierPrices(prev => ({ ...prev, [tierId]: value }));

  const handleAddProduct = () => {
    if (!selectedProduct || !productQuantity || parseFloat(productQuantity) <= 0) {
      setError('Please select a product and enter a valid quantity');
      return;
    }

    const packSize = selectedProduct.pack_size || 1;
    const perUnitCost = packSize > 0
      ? (selectedProduct.current_cost_price || 0) / packSize
      : (selectedProduct.current_cost_price || 0);

    const existingIndex = serviceProducts.findIndex(sp => sp.product_id === selectedProduct.id);
    if (existingIndex >= 0) {
      const updated = [...serviceProducts];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: parseFloat(productQuantity),
        is_optional: productIsOptional
      };
      setServiceProducts(updated);
    } else {
      setServiceProducts([...serviceProducts, {
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        quantity: parseFloat(productQuantity),
        unit_cost: perUnitCost,
        total_cost: perUnitCost * parseFloat(productQuantity),
        usage_unit: selectedProduct.unit_type || 'units',
        is_optional: productIsOptional,
        is_retail: false
      }]);
    }
    setSelectedProduct(null);
    setProductQuantity('');
    setProductIsOptional(false);
  };

  const handleUpdateProductQuantity = (productId, value) => {
    const quantity = parseFloat(value);

    setServiceProducts((prev) =>
      prev.map((sp) =>
        sp.product_id === productId
          ? {
              ...sp,
              quantity: Number.isFinite(quantity) ? quantity : 0,
              total_cost: sp.unit_cost * (Number.isFinite(quantity) ? quantity : 0),
            }
          : sp,
      ),
    );
  };

  const handleRemoveProduct = (productId) => {
    setServiceProducts((prev) => prev.filter((sp) => sp.product_id !== productId));
  };

  // Costs come from the backend, using exactly the same engine that the
  // services list and saved prices are judged against. Nothing is saved here.
  const calculateServiceCosts = async () => {
    if (!formData.duration || formData.duration <= 0) { setError('Please enter a valid service duration'); return; }
    if (tiers.length === 0) { setError(`No tiers set up. Add tiers at ${basePath}/tiers before calculating.`); return; }
    setIsCalculating(true);
    setError(null);
    try {
      const { data } = await api.post('/calculator/services/preview-costs', {
        duration: parseFloat(formData.duration),
        products: serviceProducts.map(sp => ({
          product_id: sp.product_id,
          quantity: sp.quantity,
        })),
      });

      const perTier = (data.tiers || []).map(t => ({
        tier_id: t.tier_id,
        tier_name: t.tier_name,
        hourly_rate: t.hourly_rate,
        labour_cost: t.staff_cost,
        total_cost: t.total_cost,
        recommended_price: t.recommended_price,
        current_price: parseFloat(tierPrices[t.tier_id] || 0),
      }));

      const newMeta = {};
      perTier.forEach(t => {
        newMeta[t.tier_id] = { recommended_price: t.recommended_price, total_cost: t.total_cost };
      });
      setTierMeta(newMeta);
      setCalculationResults({
        product_cost: data.product_cost,
        overhead_cost: data.overhead_cost,
        owner_profit_cost: data.owner_profit_cost,
        per_tier: perTier,
      });
      setCalculationDialogOpen(true);
    } catch (err) {
      console.error('Calculation error:', err);
      setError(err.response?.data?.error || 'Calculation failed. Please check your business settings.');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleApplyAllRecommended = () => {
    if (!calculationResults?.per_tier) return;
    const newPrices = {};
    calculationResults.per_tier.forEach(t => {
      const currentPrice = parseFloat(tierPrices[t.tier_id] || 0);
      const isUnderpriced = currentPrice <= 0 || t.recommended_price > currentPrice;
      if (isUnderpriced) {
        newPrices[t.tier_id] = t.recommended_price.toFixed(2);
      }
    });
    setTierPrices(prev => ({ ...prev, ...newPrices }));
    setCalculationDialogOpen(false);
    const appliedCount = Object.keys(newPrices).length;
    const skippedCount = calculationResults.per_tier.length - appliedCount;
    setSuccess(
      skippedCount > 0
        ? `Recommended prices applied to ${appliedCount} tier${appliedCount !== 1 ? 's' : ''} — ${skippedCount} already priced correctly`
        : 'Recommended prices applied to all tiers'
    );
    setTimeout(() => setSuccess(null), 4000);
  };

  const handleApplySingleTier = (tierResult) => {
    setTierPrices(prev => ({ ...prev, [tierResult.tier_id]: tierResult.recommended_price.toFixed(2) }));
  };

  // opts (only after the price-change dialog): { existing_bookings: 'hold'|'move', effective_date }
  const handleSave = async (opts = undefined) => {
    try {
      setSaving(true);
      setError(null);
      if (!formData.name.trim()) { setError('Service name is required'); setSaving(false); return; }
      if (!formData.duration || formData.duration <= 0) { setError('Duration must be greater than 0'); setSaving(false); return; }

      // current_price is deliberately NOT sent. The backend only updates it when
      // present, and derives the fallback from the tier prices after saving them.
      const serviceData = {
        name:        formData.name.trim(),
        description: formData.description.trim(),
        category_id: formData.category_id || null,
        duration:    parseInt(formData.duration),
      };

      let savedServiceId = serviceId;
      if (isEditMode) {
        await calculatorApi.updateService(serviceId, serviceData);
        setSuccess('Service updated successfully');
      } else {
        const result = await calculatorApi.createService(serviceData);
        savedServiceId = result.id;
        setSuccess('Service created successfully');
      }

      await api.post(`/calculator/services/${savedServiceId}/products/bulk-update`, {
        products: serviceProducts.map(sp => ({
          product_id: sp.product_id,
          quantity: sp.quantity,
          is_optional: sp.is_optional || false
        }))
      });

      // Send every tier that has a price entered, plus any tier that was priced
      // when the page loaded but has now been cleared (price 0 = remove the row).
      const tierPricePayload = tiers
        .filter(t => {
          const raw = tierPrices[t.id];
          return (raw !== '' && raw !== null && raw !== undefined) || initialPricedTierIds.includes(t.id);
        })
        .map(t => ({
          tier_id: t.id,
          price: parseFloat(tierPrices[t.id]) || 0,
          recommended_price: tierMeta[t.id]?.recommended_price ?? null,
          total_cost: tierMeta[t.id]?.total_cost ?? null,
        }));

      if (tierPricePayload.length > 0) {
        const tierBody = { prices: tierPricePayload };
        if (opts?.existing_bookings) tierBody.existing_bookings = opts.existing_bookings;
        const dateToSend = opts?.effective_date || effectiveDate;
        if (dateToSend) tierBody.effective_date = dateToSend;

        const tierRes = await api.put(`/calculator/services/${savedServiceId}/tier-prices`, tierBody);

        if (tierRes.data?.price_lock_required) {
          setPriceLockData({
            bookingsAffected: tierRes.data.bookings_affected,
            changedTiers:     tierRes.data.changed_tiers || [],
          });
          setPendingSaveWithLock(() => (o) => handleSave(o));
          setPriceLockOpen(true);
          setSaving(false);
          return;
        }
      }

      setTimeout(() => navigate(`${basePath}/services`), 1500);
    } catch (err) {
      console.error('Error saving service:', err);
      setError('Failed to save service. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePriceLockHonour   = (date) => { setPriceLockOpen(false); pendingSaveWithLock?.({ existing_bookings: 'hold', effective_date: date }); };
  const handlePriceLockApplyAll = (date) => { setPriceLockOpen(false); pendingSaveWithLock?.({ existing_bookings: 'move', effective_date: date }); };
  const handlePriceLockCancel   = () => { setPriceLockOpen(false); setPriceLockData(null); setPendingSaveWithLock(null); };

  const hasAnyCostWarning = useMemo(() => {
    return tiers.some(tier => {
      const price = parseFloat(tierPrices[tier.id] || 0);
      const cost  = parseFloat(tierMeta[tier.id]?.total_cost || 0);
      return price > 0 && cost > 0 && price < cost;
    });
  }, [tiers, tierPrices, tierMeta]);

  const canProceedToNextStep = () => {
    if (activeStep === 0) return formData.name.trim() && formData.duration > 0;
    return true;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const TierPricingCard = ({ compact = false }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Layers fontSize="small" color="primary" />
          <Typography variant="h6">Tier Prices</Typography>
          {hasAnyCostWarning && (
            <Tooltip title="One or more tiers are priced below cost">
              <Warning fontSize="small" sx={{ color: '#c62828' }} />
            </Tooltip>
          )}
        </Box>
        {!compact && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Price charged to clients per tier. Use Calculate Costs to get recommendations.
          </Typography>
        )}
        {tiers.length > 0 && (
          <TextField
            type="date" size="small" label="Price changes start on"
            value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().slice(0, 10) }}
            helperText="Leave empty for today. A later date keeps the old price until then."
            sx={{ mb: 2 }}
          />
        )}
        <Divider sx={{ mb: 2 }} />

        {tiers.length === 0 ? (
          <Alert severity="info">
            No tiers set up.{' '}
            <Button size="small" onClick={() => navigate(`${basePath}/tiers`)}
              sx={{ p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}>
              Set up tiers
            </Button>
          </Alert>
        ) : compact ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {tiers.map((tier, index) => {
              const meta  = tierMeta[tier.id] || {};
              const price = parseFloat(tierPrices[tier.id] || 0);
              const cost  = parseFloat(meta.total_cost || 0);
              const rec   = parseFloat(meta.recommended_price || 0);
              const style = getTierRowStyle(price, cost, rec);
              return (
                <Box key={tier.id}>
                  <TextField
                    fullWidth size="small" label={tier.name} type="number"
                    value={tierPrices[tier.id] ?? ''}
                    onChange={(e) => handleTierPriceChange(tier.id, e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Chip label={index + 1} size="small" sx={{
                            height: 20, fontSize: '0.7rem',
                            backgroundColor: '#667eea', color: 'white', mr: 0.5
                          }} />
                          £
                        </InputAdornment>
                      ),
                      style,
                    }}
                    placeholder="0.00"
                    helperText={tier.hourly_rate > 0 ? `£${parseFloat(tier.hourly_rate).toFixed(2)}/hr` : 'No rate set'}
                  />
                  {(rec > 0 || cost > 0) && (
                    <Box sx={{ mt: 0.5, mb: 0.5 }}>
                      {rec > 0 && <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Rec: {fmt(rec)}</Typography>}
                      {cost > 0 && <Typography variant="caption" color="text.secondary">Cost: {fmt(cost)}</Typography>}
                    </Box>
                  )}
                  <GapChip currentPrice={price} recommendedPrice={rec} totalCost={cost} />
                </Box>
              );
            })}
            <Typography variant="caption" color="text.secondary">
              Leave blank for tiers that don't offer this service.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Tier</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Recommended</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Your Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Accept</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tiers.map(tier => {
                  const meta  = tierMeta[tier.id] || {};
                  const price = parseFloat(tierPrices[tier.id] || 0);
                  const cost  = parseFloat(meta.total_cost || 0);
                  const rec   = parseFloat(meta.recommended_price || 0);
                  const style = getTierRowStyle(price, cost, rec);
                  return (
                    <TableRow key={tier.id} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ minWidth: 140 }}>
                        <Typography variant="body2" fontWeight={500}>{tier.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          £{parseFloat(tier.hourly_rate || 0).toFixed(2)}/hr
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {cost > 0
                          ? <Typography variant="body2">{fmt(cost)}</Typography>
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        {rec > 0
                          ? <Typography variant="body2" fontWeight={600} color="success.dark">{fmt(rec)}</Typography>
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small" type="number"
                          value={tierPrices[tier.id] ?? ''}
                          onChange={(e) => handleTierPriceChange(tier.id, e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment>, style }}
                          placeholder="0.00" sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <GapChip currentPrice={price} recommendedPrice={rec} totalCost={cost} />
                      </TableCell>
                      <TableCell align="right">
                        {rec > 0 && (
                          <Tooltip title={`Set price to ${fmt(rec)}`}>
                            <Button size="small" variant="outlined" color="success"
                              onClick={() => handleTierPriceChange(tier.id, rec.toFixed(2))}>
                              Accept
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(`${basePath}/services`)} sx={{ color: 'white' }} aria-label="Back to services">
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ color: 'white' }}>
                {isEditMode ? 'Edit Service' : 'Create New Service'}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {isEditMode ? 'Update service details and pricing' : 'Set up a new service with accurate cost calculation'}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button variant="contained" startIcon={<Calculate />} onClick={calculateServiceCosts}
              disabled={!formData.name || !formData.duration || isCalculating || tiers.length === 0}
              sx={{ mr: 2, backgroundColor: 'rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}>
              {isCalculating ? 'Calculating...' : 'Calculate Costs'}
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={() => handleSave()}
              disabled={saving || !formData.name || !formData.duration}
              sx={{ backgroundColor: 'white', color: '#667eea', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' } }}>
              {saving ? 'Saving...' : 'Save Service'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error   && <Alert severity="error"   onClose={() => setError(null)}   sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>{success}</Alert>}

      {linkedServiceName && (
        <Alert severity="info" icon={<CheckCircle />} sx={{ mb: 3 }}>
          Linked to <strong>{linkedServiceName}</strong> — price changes will sync automatically when saved.
        </Alert>
      )}

      {hasAnyCostWarning && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<Warning />}>
          One or more tiers are priced <strong>below cost</strong>. Review the Tier Prices table and accept the recommended prices.
        </Alert>
      )}

      {/* Stepper */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
      </Paper>

      {/* STEP 1 */}
      {activeStep === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Divider sx={{ mb: 3 }} />
                <TextField fullWidth required label="Service Name" value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)} sx={{ mb: 3 }} />
                <TextField fullWidth label="Description" multiline rows={3} value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)} sx={{ mb: 3 }} />
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="service-category-label">Category</InputLabel>
                  <Select labelId="service-category-label" value={formData.category_id} label="Category"
                    onChange={(e) => handleInputChange('category_id', e.target.value)}>
                    <MenuItem value=""><em>Uncategorized</em></MenuItem>
                    {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField fullWidth required type="number" label="Duration (minutes)" value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><AccessTime /></InputAdornment> }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <TierPricingCard compact />
          </Grid>
        </Grid>
      )}

      {/* STEP 2 */}
      {activeStep === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Products & Materials</Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={5}>
                    <Autocomplete options={products} getOptionLabel={(o) => o.name} value={selectedProduct}
                      onChange={(e, v) => setSelectedProduct(v)}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Product"
                          InputProps={{ ...params.InputProps, startAdornment: (
                            <><InputAdornment position="start"><ShoppingCart /></InputAdornment>
                              {params.InputProps.startAdornment}</>
                          )}} />
                      )}
                      renderOption={(props, option) => {
                        const packSize = option.pack_size || 1;
                        const perUnit = packSize > 0
                          ? (option.current_cost_price || 0) / packSize
                          : (option.current_cost_price || 0);
                        const unitLabel = option.unit_type || 'unit';
                        return (
                          <li {...props}>
                            <Box>
                              <Typography variant="body2">{option.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                £{perUnit.toFixed(4)} per {unitLabel}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField fullWidth type="number" label="Quantity/Usage" value={productQuantity}
                      onChange={(e) => setProductQuantity(e.target.value)}
                      inputProps={{ min: 0, step: 0.1 }} disabled={!selectedProduct} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel id="product-type-label">Type</InputLabel>
                      <Select labelId="product-type-label" value={productIsOptional} label="Type" aria-label="Product type"
                        onChange={(e) => setProductIsOptional(e.target.value)} disabled={!selectedProduct}>
                        <MenuItem value={false}>Required</MenuItem>
                        <MenuItem value={true}>Optional</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button fullWidth variant="contained" startIcon={<Add />} onClick={handleAddProduct}
                      disabled={!selectedProduct || !productQuantity} sx={{ height: 56 }}>
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {serviceProducts.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Products Added ({serviceProducts.length})</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Unit Cost</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {serviceProducts.map((sp) => (
                          <TableRow key={sp.product_id}>
                            <TableCell>{sp.product_name}</TableCell>
                            <TableCell align="right">{fmt(sp.unit_cost)}</TableCell>
                            <TableCell align="right">
                              <TextField type="number" size="small" value={sp.quantity}
                                onChange={(e) => handleUpdateProductQuantity(sp.product_id, e.target.value)}
                                inputProps={{ min: 0, step: 0.1 }} sx={{ width: 80 }} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{fmt(sp.total_cost)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={sp.is_optional ? 'Optional' : 'Required'} size="small"
                                color={sp.is_optional ? 'default' : 'primary'} />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" color="error" onClick={() => handleRemoveProduct(sp.product_id)} aria-label={`Remove ${sp.product_name}`}>
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3} align="right"><Typography variant="h6">Total Product Cost:</Typography></TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="primary.main">
                              {fmt(serviceProducts.reduce((s, sp) => s + sp.total_cost, 0))}
                            </Typography>
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}

          {serviceProducts.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">No products added yet. Services without products will only include labour and overhead costs.</Alert>
            </Grid>
          )}
        </Grid>
      )}

      {/* STEP 3 */}
      {activeStep === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Service Summary</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Service Name:</Typography>
                    <Typography variant="h6">{formData.name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Duration:</Typography>
                    <Typography variant="h6">{formData.duration} minutes</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Category:</Typography>
                    <Typography variant="body1">
                      {categories.find(c => c.id === formData.category_id)?.name || 'Uncategorized'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Products:</Typography>
                    <Typography variant="body1">{serviceProducts.length} products</Typography>
                  </Grid>
                </Grid>
                {calculationResults && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Shared Costs (all tiers)</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Products &amp; Materials</TableCell>
                          <TableCell align="right">{fmt(calculationResults.product_cost)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Overhead ({formData.duration} min)</TableCell>
                          <TableCell align="right">{fmt(calculationResults.overhead_cost)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Target profit ({formData.duration} min)</TableCell>
                          <TableCell align="right">{fmt(calculationResults.owner_profit_cost)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <TierPricingCard compact={false} />
            {tiers.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                {calculationResults?.per_tier && (
                  <Button variant="contained" color="success" onClick={handleApplyAllRecommended}>
                    Accept All Recommended Prices
                  </Button>
                )}
                {!calculationResults?.per_tier && (
                  <Button variant="outlined" startIcon={<Calculate />} onClick={calculateServiceCosts}
                    disabled={!formData.name || !formData.duration || tiers.length === 0}>
                    Calculate Costs
                  </Button>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {/* Step Navigation */}
      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Box display="flex" justifyContent="space-between">
          <Button disabled={activeStep === 0} onClick={() => setActiveStep(prev => prev - 1)}>Back</Button>
          <Box>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={() => setActiveStep(prev => prev + 1)} disabled={!canProceedToNextStep()}>
                Next
              </Button>
            ) : (
              <Button variant="contained" color="success" startIcon={<Save />} onClick={() => handleSave()}
                disabled={saving || !formData.name || !formData.duration}>
                {saving ? 'Saving...' : 'Save Service'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Calculation Results Dialog */}
      <Dialog open={calculationDialogOpen} onClose={() => setCalculationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircle color="success" />
            Cost Calculation — {formData.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {calculationResults && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="success">
                  Recommended prices calculated for all {tiers.length} tier{tiers.length !== 1 ? 's' : ''} based on actual business costs.
                  {calculationResults.per_tier.some(t => parseFloat(tierPrices[t.tier_id] || 0) > 0 && t.recommended_price <= parseFloat(tierPrices[t.tier_id])) &&
                    ' Tiers already priced above the recommended minimum are shown as correctly priced.'}
                </Alert>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Shared Costs</Typography>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Products</TableCell>
                          <TableCell align="right">{fmt(calculationResults.product_cost)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Overhead</TableCell>
                          <TableCell align="right">{fmt(calculationResults.overhead_cost)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Target profit</TableCell>
                          <TableCell align="right">{fmt(calculationResults.owner_profit_cost)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Per-Tier Breakdown</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tier</TableCell>
                          <TableCell align="right">Labour</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                          <TableCell align="right">Recommended</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {calculationResults.per_tier.map(t => {
                          const currentPrice = parseFloat(tierPrices[t.tier_id] || 0);
                          const isUnderpriced = currentPrice <= 0 || t.recommended_price > currentPrice;
                          return (
                            <TableRow key={t.tier_id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{t.tier_name}</Typography>
                                <Typography variant="caption" color="text.secondary">£{t.hourly_rate.toFixed(2)}/hr</Typography>
                              </TableCell>
                              <TableCell align="right">{fmt(t.labour_cost)}</TableCell>
                              <TableCell align="right">{fmt(t.total_cost)}</TableCell>
                              <TableCell align="right">
                                {isUnderpriced ? (
                                  <Typography fontWeight={600} color="success.dark">{fmt(t.recommended_price)}</Typography>
                                ) : (
                                  <Chip
                                    icon={<CheckCircle sx={{ fontSize: '0.85rem !important' }} />}
                                    label="Priced correctly"
                                    size="small"
                                    sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem' }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {isUnderpriced && (
                                  <Tooltip title={`Apply ${fmt(t.recommended_price)} to ${t.tier_name}`}>
                                    <Button size="small" variant="outlined" color="success"
                                      onClick={() => handleApplySingleTier(t)}>
                                      Apply
                                    </Button>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalculationDialogOpen(false)}>Close</Button>
          <Button onClick={handleApplyAllRecommended} variant="contained" color="success">
            Apply Where Underpriced
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price Lock Dialog */}
      {priceLockData && (
        <PriceLockDialog
          open={priceLockOpen}
          onClose={handlePriceLockCancel}
          onHonour={handlePriceLockHonour}
          onApplyAll={handlePriceLockApplyAll}
          bookingsAffected={priceLockData.bookingsAffected}
          changedTiers={priceLockData.changedTiers}
          effectiveDate={effectiveDate}
        />
      )}

    </Container>
  );
};

export default ServiceEditor;